import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import type { AdvertiserVerificationFailure } from "../../../zodios/apiDefinitions/api/walmart";

const isVerificationFailure = (error: unknown): error is { response: { data: AdvertiserVerificationFailure } } =>
  isAxiosError<AdvertiserVerificationFailure | undefined>(error) &&
  error.response?.status === 400 &&
  typeof error.response.data?.code === "string";

export interface AdvertiserVerificationError {
  message: string;
  /** Whether the ID could still come good, which is what makes it worth offering to save anyway. */
  retryable: boolean;
}

/** Turns a rejected advertiser ID request into copy that says what to do about it. */
export const useAdvertiserVerificationError = () => {
  const { t } = useTranslation(["onboarding"]);

  return (error: unknown): AdvertiserVerificationError => {
    if (!isVerificationFailure(error)) {
      return {
        message: t(($) => $.onboarding["Something went wrong. Check your connection and try again."]),
        retryable: false,
      };
    }

    const { retryable } = error.response.data;
    const message = ((): string => {
      switch (error.response.data.code) {
        case "access_not_granted":
          return t(
            ($) =>
              $.onboarding[
                "Trellis has not been granted access to that advertiser account. Confirm Trellis was added as a partner in Walmart Connect — a new grant can take up to 15 minutes to take effect."
              ],
          );
        case "invalid_advertiser_id":
          return t(
            ($) =>
              $.onboarding[
                "Walmart does not recognize that advertiser ID. It is the number under the account name on your Ad Center home screen."
              ],
          );
        case "rate_limited":
          return t(
            ($) => $.onboarding["Walmart is rate limiting Trellis right now. Please try again in about 10 minutes."],
          );
        case "walmart_unavailable":
        default:
          return t(($) => $.onboarding["Walmart did not respond as expected. Please try again in a few minutes."]);
      }
    })();

    return { message, retryable };
  };
};
